import { useEffect, useState } from "react";
import { Eye, Heart, MessageCircle, ShoppingBag, CalendarDays, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";

export function Bars({ series, keyName, color = "#000", testid }) {
  const max = Math.max(1, ...series.map(s => s[keyName] || 0));
  return (
    <div data-testid={testid} className="flex items-end gap-px h-16 w-full">
      {series.map(s => <div key={s.day} title={`${s.day}: ${s[keyName] || 0}`} className="flex-1 rounded-t-sm min-w-[2px]" style={{ height: `${Math.max(2, ((s[keyName] || 0) / max) * 100)}%`, background: color, opacity: (s[keyName] || 0) ? 1 : 0.15 }} />)}
    </div>
  );
}

export function Kpi({ icon: I, label, value, testid }) {
  return <div data-testid={testid} className="bg-white rounded-2xl border border-zinc-200 p-3"><div className="text-[11px] text-zinc-500 flex items-center gap-1"><I size={11} /> {label}</div><div className="font-display font-extrabold text-xl">{value ?? 0}</div></div>;
}

export function RangeChips({ value, onChange }) {
  return <div className="flex gap-2">{["7d", "30d", "90d"].map(r => <button key={r} data-testid={`insights-range-${r}`} onClick={() => onChange(r)} className="chip !text-[11px]" data-active={value === r}>{r === "7d" ? "7 dias" : r === "30d" ? "30 dias" : "90 dias"}</button>)}</div>;
}

export function SellerInsights() {
  const [range, setRange] = useState("30d");
  const [d, setD] = useState(null); const [err, setErr] = useState(false);
  useEffect(() => { setD(null); setErr(false); api.get("/insights/seller", { params: { range } }).then(r => setD(r.data)).catch(() => setErr(true)); }, [range]);
  return (
    <div data-testid="seller-insights" className="mb-6">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap"><h3 className="font-display font-bold">Insights</h3><RangeChips value={range} onChange={setRange} /></div>
      {err && <p data-testid="insights-error" className="text-xs text-zinc-500">Não foi possível carregar as estatísticas.</p>}
      {!err && !d && <div data-testid="insights-loading" className="grid grid-cols-2 sm:grid-cols-5 gap-2">{[0, 1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-white rounded-2xl border border-zinc-200 animate-pulse" />)}</div>}
      {d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            <Kpi icon={Eye} label="Visitas" value={d.totals.views} testid="kpi-views" />
            <Kpi icon={Heart} label="Favoritos" value={d.totals.favorites} testid="kpi-favorites" />
            <Kpi icon={MessageCircle} label="Mensagens" value={d.totals.messages} testid="kpi-messages" />
            <Kpi icon={ShoppingBag} label="Encomendas" value={d.totals.orders} testid="kpi-orders" />
            <Kpi icon={CalendarDays} label="Reservas" value={d.totals.reservations} testid="kpi-reservations" />
          </div>
          <div className="bg-white rounded-2xl border border-zinc-200 p-3 mb-3">
            <div className="text-[11px] text-zinc-500 mb-2">Visitas por dia</div>
            {d.totals.views === 0 ? <p data-testid="insights-empty" className="text-xs text-zinc-400 italic py-4 text-center">Ainda sem visitas neste período. Partilha a tua loja para começar.</p> : <Bars series={d.series} keyName="views" testid="insights-chart" />}
          </div>
          {d.top_items?.length > 0 && (
            <div className="bg-white rounded-2xl border border-zinc-200 p-3 mb-3" data-testid="insights-top">
              <div className="text-[11px] text-zinc-500 mb-2">Mais vistos</div>
              {d.top_items.map(it => <div key={it.target_id} className="flex justify-between text-xs py-1 border-b border-zinc-100 last:border-0"><span className="truncate">{it.title}</span><span className="text-zinc-500 shrink-0">{it.views} visitas · {it.favorites} ♥ · {it.messages} msg</span></div>)}
            </div>
          )}
          {d.tip && <p data-testid="insights-tip" className="text-[11px] text-zinc-600 flex items-start gap-1.5 bg-amber-50 border border-amber-100 rounded-xl p-2.5"><Lightbulb size={12} className="shrink-0 mt-px text-amber-600" /> {d.tip}</p>}
          <p className="text-[10px] text-zinc-400 mt-2">Contagens agregadas e anónimas. Não inclui valores de vendas nem identifica visitantes.</p>
        </>
      )}
    </div>
  );
}
